import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Phone, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { ParticleBackground } from "@/components/ParticleBackground";
import { useToast } from "@/hooks/use-toast";
import Page from "@/components/layout/Page";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";

const contactFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: "Name must be at least 2 characters" })
    .max(100, { message: "Name must be less than 100 characters" }),
  email: z
    .string()
    .trim()
    .email({ message: "Please enter a valid email address" })
    .max(255, { message: "Email must be less than 255 characters" }),
  phone: z
    .string()
    .trim()
    .min(10, { message: "Please enter a valid phone number" })
    .max(20, { message: "Phone number is too long" })
    .regex(/^[0-9\s\-\+\(\)]+$/, {
      message: "Phone number can only contain numbers, spaces, and +()-",
    }),
  product: z.string().optional(),
  message: z
    .string()
    .trim()
    .min(10, { message: "Message must be at least 10 characters" })
    .max(2000, { message: "Message must be less than 2000 characters" }),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

const productOptions = [
  { value: "general", label: "General enquiry" },
  { value: "Samboard", label: "Samboards" },
  { value: "Mini Samboard", label: "Mini Samboards" },
  { value: "Library Squares", label: "Library Squares" },
  { value: "Sensory Pads", label: "Sensory Pads" },
  { value: "Fidget Squares", label: "Fidget Squares" },
  { value: "Access to Work Kit", label: "Access to Work Kit" },
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      product: "general",
      message: "",
    },
  });

  const selectedProduct = watch("product");

  // Pre-fill product from query params
  useEffect(() => {
    const productParam = searchParams.get("product");
    if (productParam) {
      setValue("product", productParam);
    }
  }, [searchParams, setValue]);

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            name: data.name,
            email: data.email,
            phone: data.phone,
            product: data.product,
            message: data.message,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setIsSuccess(true);

      toast({
        title: "Message sent!",
        description:
          "We've received your message and will respond within 24 hours.",
      });

      // Reset form after 2 seconds
      setTimeout(() => {
        reset();
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Something went wrong",
        description:
          "Please try again or contact us directly at samboards19@aol.com",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact Us | Get in Touch"
        description="Contact Samboards for enquiries about sensory tools, bulk orders, Access to Work applications, and product information. UK-based team ready to help."
        canonical="/contact"
        keywords="contact samboards, sensory tools enquiries, bulk orders, access to work support"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Contact", url: "/contact" }
      ]} />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground className="absolute inset-0 -z-10" />

        <Page>
        <div className="pb-24">
          <div className="text-center mb-12 sm:mb-16">
            <h1 className="text-[36px] sm:text-[40px] lg:text-[54px] leading-[1.1] font-bold mb-4 page-title-gradient">
              Get in Touch
            </h1>
            <p className="text-lg sm:text-xl text-slate-200/80">
              Have questions about our products? We're here to help.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-16">
            {/* Contact Form */}
            <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-6 sm:p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
              <h2 className="text-xl sm:text-2xl font-semibold mb-6 text-slate-100">
                Send us a message
              </h2>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 sm:space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-200">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    {...register("name")}
                    placeholder="Your name"
                    className="bg-slate-900/50 border-white/10 text-slate-100 placeholder:text-slate-400 rounded-xl"
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-400">{errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-200">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    {...register("email")}
                    placeholder="your.email@example.com"
                    className="bg-slate-900/50 border-white/10 text-slate-100 placeholder:text-slate-400 rounded-xl"
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-400">{errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-200">
                    Phone *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    {...register("phone")}
                    placeholder="+44 1234 567890"
                    className="bg-slate-900/50 border-white/10 text-slate-100 placeholder:text-slate-400 rounded-xl"
                    disabled={isSubmitting}
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-400">{errors.phone.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="product" className="text-slate-200">
                    Product Interest
                  </Label>
                  <Select
                    value={selectedProduct}
                    onValueChange={(value) => setValue("product", value)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="bg-slate-900/50 border-white/10 text-slate-100 rounded-xl">
                      <SelectValue placeholder="Select a product (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10">
                      {productOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="text-slate-100 focus:bg-slate-800 focus:text-emerald-300"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.product && (
                    <p className="text-sm text-red-400">
                      {errors.product.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-200">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    {...register("message")}
                    placeholder="Tell us how we can help..."
                    rows={6}
                    className="bg-slate-900/50 border-white/10 text-slate-100 placeholder:text-slate-400 rounded-xl resize-none"
                    disabled={isSubmitting}
                  />
                  {errors.message && (
                    <p className="text-sm text-red-400">
                      {errors.message.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting || isSuccess}
                  className="w-full bg-gradient-to-r from-emerald-400 to-teal-400 text-slate-900 hover:from-emerald-300 hover:to-teal-300 rounded-full shadow-[0_0_40px_rgba(16,185,129,0.3)] hover:shadow-[0_0_55px_rgba(16,185,129,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : isSuccess ? (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Sent!
                    </>
                  ) : (
                    "Send Message"
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold mb-6 text-slate-100">
                  Contact Information
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Mail className="w-6 h-6 text-emerald-400 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1 text-slate-100">
                        Email
                      </h3>
                      <a
                        href="mailto:samboards19@aol.com"
                        className="text-slate-300/70 hover:text-emerald-300 transition-colors"
                      >
                        samboards19@aol.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Phone className="w-6 h-6 text-emerald-400 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1 text-slate-100">
                        Phone
                      </h3>
                      <p className="text-slate-300/70">
                        Available on request
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-emerald-400 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1 text-slate-100">
                        Location
                      </h3>
                      <p className="text-slate-300/70">
                        Based in the UK
                        <br />
                        Shipping nationwide
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                <h3 className="text-lg font-semibold mb-3 text-slate-100">
                  Response Time
                </h3>
                <p className="text-slate-300/70 leading-relaxed">
                  We typically respond within 24 hours during business days. For
                  urgent enquiries, please call us directly.
                </p>
              </div>

              <div className="rounded-3xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 backdrop-blur-xl border border-emerald-500/20 p-6 shadow-[0_8px_32px_rgba(16,185,129,0.15)]">
                <h3 className="text-lg font-semibold mb-3 text-emerald-300">
                  Bulk Orders & Special Requirements
                </h3>
                <p className="text-slate-300/70 leading-relaxed text-sm">
                  We support Access to Work applications, education settings,
                  healthcare environments, and bulk purchasing for organisations.
                  Let us know your needs in the message above.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Page>
      </div>
    </>
  );
}
